import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  AArrowDown,
  AArrowUp,
  AudioLines,
  Contrast,
  ChevronDown,
  ChevronUp,
  Check,
  Link2,
  Pause,
  RotateCcw,
  ScanLine,
  Space,
  Type,
  X,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { isDefault, TEXT_SIZES, type ToggleKey } from '@/a11y/preferences'
import { useA11yPreferences } from '@/a11y/useA11yPreferences'
import { useReadAloud } from '@/a11y/useReadAloud'
import { ReadAloudBar } from './ReadAloudBar'
import { AccessibilityIcon } from './AccessibilityIcon'

/** Dispatch on window to open the menu from anywhere (e.g. the Accessibility page). */
export const OPEN_A11Y_MENU_EVENT = 'gia:a11y-open'

const TOGGLES: { key: ToggleKey; label: string; hint: string; icon: LucideIcon; pointerOnly?: boolean }[] = [
  { key: 'highContrast', label: 'High contrast', hint: 'Stronger text and backgrounds', icon: Contrast },
  { key: 'readableFont', label: 'Readable font', hint: 'Atkinson Hyperlegible, made for low vision', icon: Type },
  { key: 'textSpacing', label: 'Text spacing', hint: 'More room between lines, letters and words', icon: Space },
  { key: 'highlightLinks', label: 'Highlight links', hint: 'Make every link easy to spot', icon: Link2 },
  { key: 'reduceMotion', label: 'Reduce motion', hint: 'Stop animations and auto-play', icon: Pause },
  { key: 'readingGuide', label: 'Reading guide', hint: 'A band that follows your pointer', icon: ScanLine, pointerOnly: true },
]

export function AccessibilityMenu() {
  const { prefs, update, reset } = useA11yPreferences()
  const reader = useReadAloud()
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [finePointer, setFinePointer] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const sizeId = useId()
  const toolsId = useId()

  useEffect(() => {
    setFinePointer(window.matchMedia('(pointer: fine)').matches)
    const openMenu = () => setOpen(true)
    window.addEventListener(OPEN_A11Y_MENU_EVENT, openMenu)
    return () => window.removeEventListener(OPEN_A11Y_MENU_EVENT, openMenu)
  }, [])

  const close = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  // Non-modal: the page remains usable for trying settings in context.
  useEffect(() => {
    if (open) panelRef.current?.focus({ preventScroll: true })
    else setExpanded(false)
  }, [open])

  // Leave room to scroll the final page content above the dock, too.
  useEffect(() => {
    if (!open || !panelRef.current) return
    const root = document.documentElement
    const panel = panelRef.current
    const measure = () => root.style.setProperty('--a11y-panel-height', `${panel.getBoundingClientRect().height}px`)
    root.setAttribute('data-a11y-panel', '')
    const observer = new ResizeObserver(measure)
    observer.observe(panel)
    measure()
    return () => {
      observer.disconnect()
      root.removeAttribute('data-a11y-panel')
      root.style.removeProperty('--a11y-panel-height')
    }
  }, [open])

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault()
      close()
    }
  }

  const sizeIndex = TEXT_SIZES.indexOf(prefs.textSize)
  const customized = !isDefault(prefs)

  function startReading() {
    setOpen(false)
    triggerRef.current?.focus()
    reader.start()
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={customized ? 'Accessibility settings (customized)' : 'Accessibility settings'}
        title="Accessibility"
        // Phones/tablets: a filled brand-blue button so it reads as a feature,
        // not a decoration. Desktop (xl, full nav visible): quieter outline.
        className="relative inline-flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full bg-brand-strong text-surface shadow-[0_4px_14px_-4px_rgb(35_119_201_/_0.6)] transition-colors hover:bg-ink-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 xl:bg-transparent xl:text-ink-deep xl:shadow-none xl:ring-1 xl:ring-ink-deep/15 xl:hover:bg-surface-alt"
      >
        <AccessibilityIcon className="h-6 w-6" />
        {customized && (
          <span aria-hidden className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-[#ffe94d] ring-2 ring-surface" />
        )}
      </button>

      {open && createPortal(
        <>
          <div
            ref={panelRef}
            role="dialog"
            aria-labelledby={titleId}
            tabIndex={-1}
            onKeyDown={onKeyDown}
            className={cn(
              'fixed z-50 flex animate-fade-up flex-col overflow-hidden border border-hairline bg-surface text-ink-deep shadow-[0_24px_70px_-20px_rgb(44_41_38_/_0.45)] focus:outline-none',
              // phone: bottom sheet · tablet/desktop: popover under the header
              'inset-x-0 bottom-0 rounded-t-2xl pb-[env(safe-area-inset-bottom)]',
              expanded ? 'max-h-[82dvh]' : 'max-h-[46dvh]',
              'sm:inset-x-auto sm:bottom-auto sm:right-4 sm:top-[4.5rem] sm:max-h-[calc(100vh-5.5rem)] sm:w-[23rem] sm:rounded-xl sm:pb-0',
              'lg:top-[7.5rem] lg:max-h-[calc(100vh-8.5rem)]',
            )}
          >
            <div aria-hidden className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-ink-deep/15 sm:hidden" />
            <div className="flex shrink-0 items-center justify-between gap-2 px-4 py-1 sm:px-5 sm:py-4">
              <div className="min-w-0 flex-1">
                <h2 id={titleId} className="break-words font-display text-base font-extrabold text-ink-deep sm:text-xl">
                  Accessibility
                </h2>
                <p className="mt-0.5 hidden text-sm text-ink-muted sm:block">Adjust the site to suit you. Saved on this device.</p>
              </div>
              <button
                type="button"
                aria-expanded={expanded}
                aria-controls={toolsId}
                onClick={() => setExpanded((value) => !value)}
                className="ml-auto inline-flex min-h-11 items-center gap-1 rounded-lg px-2 text-xs font-bold hover:bg-surface-alt focus-visible:ring-2 focus-visible:ring-brand sm:hidden"
              >
                {expanded ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronUp className="h-4 w-4" aria-hidden />}
                {expanded ? 'See page' : 'More details'}
              </button>
              <button
                type="button"
                onClick={close}
                aria-label="Close accessibility settings"
                className="-mr-2 -mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full hover:bg-surface-alt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>

            <div id={toolsId} className="min-h-0 overflow-y-auto overscroll-contain px-3 pb-2 sm:pb-3">
              <p className="px-2 pb-1 text-xs text-ink-muted sm:hidden">Try a setting. Scroll the page above to see it change.</p>
              <div className="space-y-1">
                {/* Text size stepper */}
                <div role="group" aria-labelledby={sizeId} className="flex min-h-12 sm:min-h-14 flex-wrap items-center justify-between gap-3 rounded-lg px-2">
                  <span id={sizeId} className="flex items-center gap-2 text-sm sm:gap-3 sm:text-base font-semibold">
                    <IconBox active={prefs.textSize !== 100}>
                      <AArrowUp className="h-5 w-5" aria-hidden />
                    </IconBox>
                    Text size
                  </span>
                  <div className="ml-auto flex items-center gap-1">
                    <StepButton
                      label="Decrease text size"
                      disabled={sizeIndex <= 0}
                      onClick={() => update({ textSize: TEXT_SIZES[sizeIndex - 1] })}
                    >
                      <AArrowDown className="h-5 w-5" aria-hidden />
                    </StepButton>
                    <output aria-live="polite" className="w-12 text-center text-sm font-bold tabular">
                      {prefs.textSize}%
                    </output>
                    <StepButton
                      label="Increase text size"
                      disabled={sizeIndex >= TEXT_SIZES.length - 1}
                      onClick={() => update({ textSize: TEXT_SIZES[sizeIndex + 1] })}
                    >
                      <AArrowUp className="h-5 w-5" aria-hidden />
                    </StepButton>
                  </div>
                </div>

                <div className={cn('gap-1', expanded ? 'space-y-1' : prefs.textSize >= 130 ? 'grid grid-cols-1 sm:block sm:space-y-1' : 'grid grid-cols-2 sm:block sm:space-y-1')}>
                  {TOGGLES.filter((t) => !t.pointerOnly || finePointer).map(({ key, label, hint, icon: Icon }) => (
                    <button
                      key={key}
                      type="button"
                      role="switch"
                      aria-checked={prefs[key]}
                      onClick={() => update({ [key]: !prefs[key] })}
                      className={cn(
                        'flex min-h-11 w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors hover:bg-surface-alt/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand sm:min-h-14 sm:gap-3',
                        !expanded && 'border border-hairline sm:border-0',
                        prefs[key] && 'bg-brand-soft',
                      )}
                    >
                      <IconBox active={prefs[key]}>
                        <Icon className="h-5 w-5" aria-hidden />
                      </IconBox>
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs font-semibold sm:text-base">{label}</span>
                        <span className={cn('text-sm leading-snug text-ink-muted sm:block', expanded ? 'block' : 'hidden')}>{hint}</span>
                      </span>
                      <span className={cn('sm:inline-flex', expanded ? 'inline-flex' : 'hidden')}><Switch on={prefs[key]} /></span>
                      {!expanded && <span className="w-4 shrink-0 sm:hidden" aria-hidden>{prefs[key] && <Check className="h-4 w-4 text-brand-strong" />}</span>}
                    </button>
                  ))}
                </div>
              </div>

              {reader.supported && (
                <div className="mt-2 border-t border-hairline px-2 py-2 sm:px-5 sm:py-4">
                  <button
                    type="button"
                    onClick={reader.status === 'idle' ? startReading : reader.stop}
                    className="flex min-h-11 sm:min-h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-ink-deep px-4 font-display text-sm font-extrabold uppercase tracking-[0.06em] transition-colors hover:bg-ink-deep hover:text-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                  >
                    <AudioLines className="h-5 w-5" aria-hidden />
                    {reader.status === 'idle' ? 'Read this page aloud' : 'Stop reading'}
                  </button>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-x-3 border-t border-hairline px-1 py-1 sm:px-2 sm:py-3">
                <button
                  type="button"
                  onClick={reset}
                  disabled={!customized}
                  className="inline-flex min-h-11 items-center gap-2 text-sm font-bold text-ink-deep hover:text-brand-strong disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  <RotateCcw className="h-4 w-4" aria-hidden />
                  Reset all
                </button>
                <a
                  href="/accessibility"
                  className="inline-flex min-h-11 items-center text-xs sm:text-sm font-bold text-ink-deep underline decoration-1 underline-offset-4 hover:text-brand-strong"
                >
                  Accessibility statement
                </a>
              </div>
            </div>
          </div>
        </>,
        document.body,
      )}

      {!open && (reader.status !== 'idle' || reader.error) && createPortal(<ReadAloudBar reader={reader} />, document.body)}
    </>
  )
}

function IconBox({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex h-7 w-7 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-lg transition-colors',
        active ? 'bg-brand-strong text-surface' : 'bg-surface-alt text-ink-deep',
      )}
    >
      {children}
    </span>
  )
}

function Switch({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border-2 transition-colors',
        on ? 'border-brand-strong bg-brand-strong' : 'border-ink-deep/30 bg-surface',
      )}
    >
      <span
        className={cn(
          'absolute h-5 w-5 rounded-full shadow transition-transform',
          on ? 'translate-x-[1.375rem] bg-surface' : 'translate-x-0.5 bg-ink-deep/55',
        )}
      />
    </span>
  )
}

function StepButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-11 w-11 items-center justify-center rounded-lg border-2 border-ink-deep/20 text-ink-deep transition-colors hover:border-ink-deep disabled:cursor-not-allowed disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      {children}
    </button>
  )
}
